import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as amplitude from "@amplitude/analytics-browser";
import { Dropdown, DropdownMenu, DropdownButton, DropdownMenuItem } from '@unified-checkout/design-system';
import { LeftToLine, RightToLine } from '@unified-checkout/design-system/icons/index';
import { PaymobIcon, PaymobLogo } from '@unified-checkout/design-system/icons/paymob_logo';
import { ACCOUNT_EVENTS } from '@app/dashboard/constants/tracking_events/account';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { useNavigationStore } from '@app/dashboard/store/navigation';
import { useUserInfoStore } from '@app/dashboard/store/userInfo';
import { accountBarLinks, navLinks } from '@app/dashboard/services/inner_layout/nav-links';
import type { ISideMenu } from '@app/dashboard/types/innerLayout/side-menu';
import type { TAccountBarActions, TInnerRoute } from '@app/dashboard/types/innerLayout/nav-links';
import { isNavItemHidden } from '@app/dashboard/utils/navItemsSupportedRoutes';
import { getCurrentPath } from '@app/dashboard/utils/getCurrentPath';
import { getJoyRideElementWrapperId } from '../DashboardGuide/utils';
import { NavItem } from '../NavItem';
import { AccountBar } from '../AccountBar';
import styles from "./side-menu.module.scss";


const SideMenu = (
  {
    isSideMenuCollpased,
    setIsSideMenuCollpased,
    editProfileHandler,
    logoutHandler
  }
    : ISideMenu
) => {
  const { t } = useTranslation();
  const { activeLink } = useNavigationStore();
  const navigate = useNavigate();
  const location = useLocation();

  const { userInfo } = useUserInfoStore();
  const { logo, userName, shortName, profileType, mId, staffId } = userInfo;

  const visibleNavLinks = useMemo(
    () => navLinks(t)
      .filter(navLink => !navLink.mobileOnlyLinks && !isNavItemHidden(location.pathname as TInnerRoute, navLink.hiddenRoutes || []))
      .map(navLink => ({
        ...navLink,
        subItems: navLink.subLinks?.map((subLink) => ({
          icon: subLink.icon,
          id: subLink.id,
          label: subLink.label,
          route: subLink.route,
          isExternalRoute: subLink.isExternalRoute,
          selectable: subLink.selectable,
          active: activeLink.sub === subLink.label,
          hiddenRoutes: subLink.hiddenRoutes,
          specialAction: subLink.specialAction,
          tourElementId: subLink.tourElementId
        }))
      })),
    [activeLink.sub, location.pathname, t]
  );

  const accountBarHandlers: { [key in TAccountBarActions]: () => void } = {
    'edit-profile': editProfileHandler,
    'logout': logoutHandler
  }

  const collapseBtnClickHandler = () => {
    setIsSideMenuCollpased(!isSideMenuCollpased)
  };

  const logoClickHandler = () => {
    navigate(INNER_ROUTES.HOME);
  };

  const accountMenuOpenHandler = () => {
    amplitude.track(ACCOUNT_EVENTS.PROFILE_MENU_OPENED, {
      [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath()
    });
  };

  const AccountBarInstance = <AccountBar
    collapsed={isSideMenuCollpased}
    handlers={{
      logout: logoutHandler,
      editProfile: editProfileHandler
    }}
    userName={userName || ""}
    role={profileType}
    mId={mId}
    staffId={staffId}
    {
    ...(
      logo ?
        {
          type: 'img',
          imgUrl: logo
        }
        :
        {
          type: 'text',
          userShortName: shortName
        }
    )
    }
  />;

  return (
    <aside
      className={`${styles["side-menu"]}  ${getJoyRideElementWrapperId([4, 5])}`}
      data-side-menu-collapsed={isSideMenuCollpased}
    >

      <div className={styles["side-menu__collapse-expand-btn"]} onClick={collapseBtnClickHandler}>
        {isSideMenuCollpased ? RightToLine : LeftToLine}
      </div>

      <div className={styles["side-menu__logo"]} onClick={logoClickHandler}>
        {isSideMenuCollpased ? PaymobIcon : PaymobLogo}
      </div>

      <div className={styles["side-menu__nav-links"]}>
        {
          visibleNavLinks.map((navLink, i: number) => {
              return (
                <NavItem
                  key={`nav-link-${i}`}
                  icon={navLink.icon}
                  id={navLink.id}
                  label={navLink.label}
                  route={navLink.route}
                  isExternalRoute={navLink.isExternalRoute}
                  active={activeLink.nav === navLink.label}
                  tourElementId={navLink.tourElementId}
                  subItems={navLink.subItems}
                  collapsed={isSideMenuCollpased}
                  hasSubLinks={!!navLink?.subLinks}
                  specialAction={navLink.specialAction}
                  selectable={navLink.selectable}
                />
              )
            })
        }
      </div>

      <div className={styles["side-menu__footer"]}>
        {
          isSideMenuCollpased ?
            <Dropdown>
              <DropdownButton
                renderAs={AccountBarInstance}
              />

              <DropdownMenu
                type="account"
                {
                ...(
                  logo ?
                    {
                      avatarType: 'img',
                      imgUrl: logo
                    }
                    :
                    {
                      avatarType: 'text',
                      userShortName: shortName
                    }
                )
                }
                userName={userName || ""}
                role={profileType}
                mId={mId && `${t('innerLayout.mid')}: ${mId}`}
                staffId={mId !== staffId ? `${t('innerLayout.staffId')}: ${staffId}` : ""}
                imgUrl={logo}
                horizontalPlacement="start"
                verticalPlacement="top"
                width="fit-content"
                onToggleHandler={(open) => open && accountMenuOpenHandler()}
              >
                {accountBarLinks(t).map((item, i: number) => (
                  <DropdownMenuItem
                    text={item.label}
                    icon={item.icon}
                    onClickHandler={accountBarHandlers[item.action]}
                    key={`profile-box-item-${i}`}
                  />
                ))}
              </DropdownMenu>
            </Dropdown>
            :
            AccountBarInstance
        }
      </div>

    </aside>
  )
}

export default SideMenu;
